import * as Dialog from "@radix-ui/react-dialog";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "~/common/redesign-v1/ui";
import { CenteredDialog } from "~/components/ui/centered-dialog";
import { Spinner } from "~/components/ui/spinner";
import { API_MANUALS_GET_STATIC } from "~/config";
import { useLoadingDelay } from "~/hooks/use-loading-delay";

import { useCloseToast } from "./toaster";
import { ToastCloseIntent, ToastTypeTag } from "./toasts-api";

const PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID = 49;

export function PriceListUpdateDialog({ isOpen: isOpenProp }: { isOpen: boolean }) {
  const [isOpen, setIsOpen] = useState(true);
  const { closeToast, isCloseLoading } = useCloseToast({
    tag: ToastTypeTag.PRICE_LIST_UPDATE_JUNE_2025,
  });
  const isCloseLoadingDelayed = useLoadingDelay(isCloseLoading, { delay: 200 });

  function handleAction() {
    void closeToast({ intent: ToastCloseIntent.ACTION });
    setIsOpen(false);
  }

  function handleClose() {
    void closeToast({ intent: ToastCloseIntent.CLOSE });
    setIsOpen(false);
  }

  return (
    <CenteredDialog
      isOpen={isOpen && isOpenProp}
      onOpenChange={setIsOpen}
      closeProps={{ onClick: handleClose }}
      className="tw-space-y-4"
    >
      {/* heading-4 (20/24, weight 500) — the shared modal-shell title style (`CenteredDialog`). */}
      <Dialog.Title className="tw-m-0 tw-pr-8 tw-text-xl tw-font-medium tw-text-foreground">
        Уважаемые партнёры!
      </Dialog.Title>

      <Dialog.Description asChild>
        <div>
          С радостью сообщаем, что в скором времени для вас будут доступны обновленные курсы 3D
          Smile – теперь они стали еще удобнее и эффективнее!
        </div>
      </Dialog.Description>

      <div className="tw-font-bold">Что изменилось:</div>

      <ul className="tw-space-y-2.5 tw-pl-3">
        <li>
          Индивидуальный подход – новое наполнение курсов 3D Smile лучше учитывает потребности
          каждого врача и пациента.
        </li>

        <li>
          Повышена клиническая эффективность – обновленные протоколы для ещё более впечатляющих
          результатов.
        </li>

        <li>
          Оптимизированные сроки – теперь вы сможете достигать целей быстрее и с максимальной
          выгодой для клиники и врача.
        </li>
      </ul>

      <div>
        Мы учли все ваши пожелания, внедрили значительные улучшения, поэтому произошла
        незначительная корректировка стоимости. Ознакомиться с новым наполнением курсов и
        стоимостью, которые вступят в силу с 01 июля 2025 года, вы можете в Личном кабинете, перейдя
        по{" "}
        <a
          href={API_MANUALS_GET_STATIC("document", PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleAction}
        >
          ссылке
        </a>
        .
      </div>

      <div>Благодарим за ваше доверие и совместную работу!</div>

      <div>С уважением, 3D Smile</div>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-2 sm:tw-flex-row sm:tw-gap-3">
        <Button size="lg" className="sm:tw-min-w-[130px]" onClick={handleAction} asChild>
          <a
            href={API_MANUALS_GET_STATIC("document", PRICE_LIST_UPDATE_JUNE_2025_MANUAL_ID)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="learn_more" />
          </a>
        </Button>

        <Button
          type="button"
          variant="outline"
          size="lg"
          className="sm:tw-min-w-[130px]"
          disabled={isCloseLoadingDelayed}
          onClick={handleClose}
        >
          {isCloseLoadingDelayed ? <Spinner /> : <FormattedMessage id="alert.close" />}
        </Button>
      </div>
    </CenteredDialog>
  );
}
